import React from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Check, ChevronLeft, Gift } from "lucide-react";
import MobileViewContainer from "../../Components/MobileViewContainer";
import { useAuthContext } from "../../Components/AuthContext";
import { couponService } from "../../api/services/coupon.service";
import { voucherService } from "../../api/services/voucher.service";
import { eventService } from "../../api/services/event.service";

const TAB = {
  VOUCHER: "voucher",
  COUPON: "coupon",
};

const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// 할인율/금액을 좌측에 크게 노출하기 위해 값과 단위를 분리
const getDiscountValue = (couponProduct) => {
  if (couponProduct.discount_type === "price") {
    return { value: couponProduct.discount_amount.toLocaleString(), unit: "원" };
  }
  return { value: String(couponProduct.discount_rate), unit: "%" };
};

const resolveActiveTab = (tab) => (tab === TAB.COUPON ? TAB.COUPON : TAB.VOUCHER);

const UNUSED_STATE = "사용안함";

// 마이페이지 쿠폰함에 미사용 쿠폰/무료쿠폰이 하나라도 있는지 확인 (있으면 조기 종료)
const hasUnusedUserCoupons = async () => {
  let page = 1;
  while (page) {
    const res = await couponService.getCouponsByPage(page);
    const pageData = res.data || {};
    const results = pageData.results || [];
    if (results.some((coupon) => (coupon.state || UNUSED_STATE) === UNUSED_STATE)) {
      return true;
    }
    if (!pageData.next) break;
    const match = pageData.next.match(/page=(\d+)/);
    if (!match) break;
    const next = Number(match[1]);
    if (!next || Number.isNaN(next)) break;
    page = next;
  }

  const voucherRes = await voucherService.getVoucherSendData();
  const vouchers = voucherRes.data?.vouchers || [];
  return vouchers.some((voucher) => (voucher.state || UNUSED_STATE) === UNUSED_STATE);
};

// 소진 / NEW 상태 배지
const StatusBadges = ({ isNew, soldOut }) => (
  <>
    {isNew ? (
      <span className="rounded-full bg-[#e16036] px-1.5 py-0.5 text-[10px] font-black leading-none text-white">NEW</span>
    ) : null}
    {soldOut ? (
      <span className="rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-bold leading-none text-gray-700">소진</span>
    ) : null}
  </>
);

// 받기 버튼 (받기 → 완료 시 "다운로드 완료")
const GetButton = ({ done, loading, disabled, onClick }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={`shrink-0 min-w-[108px] whitespace-nowrap rounded-lg border px-3.5 py-2.5 text-center text-xs font-bold transition active:scale-[0.96] focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 ${
      done
        ? "border-gray-700 bg-white text-gray-700"
        : "border-gray-900 bg-gray-900 text-white text-gray-900 disabled:border-gray-200 disabled:bg-white disabled:text-gray-300"
    }`}
  >
    {done ? "다운로드 완료" : loading ? "처리중" : "받기"}
  </button>
);

const CouponBookDownloadPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { accessToken } = useAuthContext();

  const activeTab = resolveActiveTab(searchParams.get("tab"));

  const [couponItems, setCouponItems] = React.useState([]);
  const [voucherItems, setVoucherItems] = React.useState([]);
  const [voucherStatusMap, setVoucherStatusMap] = React.useState({});
  const [couponLoading, setCouponLoading] = React.useState(false);
  const [voucherLoading, setVoucherLoading] = React.useState(false);
  const [downloadingKey, setDownloadingKey] = React.useState(null);
  const [batchLoading, setBatchLoading] = React.useState(false);
  const [hasUnused, setHasUnused] = React.useState(false);

  const fetchCouponItems = React.useCallback(async () => {
    setCouponLoading(true);
    try {
      const response = await couponService.getCouponBookPublic();
      const items = response.data || [];
      setCouponItems(items);
    } catch (error) {
      console.error(error);
    } finally {
      setCouponLoading(false);
    }
  }, [accessToken]);

  const fetchVoucherStatuses = React.useCallback(async (rules) => {
    if (!accessToken || !rules.length) {
      setVoucherStatusMap({});
      return;
    }

    const pairs = await Promise.all(
      rules.map(async (rule) => {
        try {
          const response = await eventService.checkBook(String(rule.id));
          return [rule.id, response.data];
        } catch (error) {
          console.error(error);
          return [rule.id, { downloaded: false, remain: rule.stock }];
        }
      })
    );

    setVoucherStatusMap(
      pairs.reduce((acc, [id, status]) => {
        acc[id] = status;
        return acc;
      }, {})
    );
  }, [accessToken]);

  const fetchVoucherItems = React.useCallback(async () => {
    setVoucherLoading(true);
    try {
      const response = await eventService.listVoucherBookRules();
      const today = getLocalDateString();
      const activeRules = (response.data || []).filter(
        (rule) => rule.state === "노출중" && rule.start_date <= today && rule.end_date >= today
      );
      setVoucherItems(activeRules);
      await fetchVoucherStatuses(activeRules);
    } catch (error) {
      console.error(error);
    } finally {
      setVoucherLoading(false);
    }
  }, [fetchVoucherStatuses]);

  // 진입 시 스크롤을 최상단으로. 이 페이지는 Navbar를 쓰지 않아 라우트 이동 시 스크롤 리셋 혜택을 못 받으므로,
  // 이전 페이지의 스크롤 위치가 유지되면 sticky 헤더에 콘텐츠(제목·안내 문구)가 가려진다.
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    fetchCouponItems();
    fetchVoucherItems();
  }, [fetchCouponItems, fetchVoucherItems]);

  React.useEffect(() => {
    if (!accessToken) {
      setHasUnused(false);
      return;
    }
    let active = true;
    hasUnusedUserCoupons()
      .then((result) => {
        if (active) setHasUnused(result);
      })
      .catch((error) => {
        console.error(error);
      });
    return () => {
      active = false;
    };
  }, [accessToken]);

  const moveToLogin = () => {
    const redirectPath = `${location.pathname}${location.search || ""}`;
    navigate(`/login?path=${redirectPath}`);
  };

  const handleTabChange = (tab) => {
    setSearchParams({ tab }, { replace: true });
  };

  const handleCouponDownload = async (item) => {
    if (!accessToken) {
      moveToLogin();
      return;
    }

    if (!item.downloadable) {
      toast.error("다운로드 대상이 아닌 할인쿠폰입니다.");
      return;
    }

    if (item.downloaded) {
      toast.error("이미 다운로드 받은 할인쿠폰입니다.");
      return;
    }

    const key = `coupon-${item.id}`;
    try {
      setDownloadingKey(key);
      await couponService.postCouponDownById(item.id);
      toast.success("할인쿠폰을 다운로드 받았습니다.");
      await fetchCouponItems();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.error || "다운로드에 실패했습니다.");
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleVoucherDownload = async (item) => {
    if (!accessToken) {
      moveToLogin();
      return;
    }

    const status = voucherStatusMap[item.id];
    if (status?.downloaded) {
      toast.error("이미 다운로드 받은 무료쿠폰입니다.");
      return;
    }

    const key = `voucher-${item.id}`;
    try {
      setDownloadingKey(key);
      await eventService.downloadVoucherBook(item.id);
      toast.success("무료쿠폰을 다운로드 받았습니다.");
      await fetchVoucherItems();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.error || "다운로드에 실패했습니다.");
    } finally {
      setDownloadingKey(null);
    }
  };

  const isCouponTab = activeTab === TAB.COUPON;
  const isLoading = isCouponTab ? couponLoading : voucherLoading;

  // 안 받은(다운로드 가능한) 쿠폰 목록 — 상단 요약 및 "모두 받기" 대상
  const couponTargets = couponItems.filter((item) => item.downloadable && !item.downloaded);
  const voucherTargets = voucherItems.filter((item) => !(voucherStatusMap[item.id]?.downloaded));
  const targets = isCouponTab ? couponTargets : voucherTargets;
  const currentItems = isCouponTab ? couponItems : voucherItems;

  // 전용 배치 API가 없어 낱개 다운로드 API를 반복 호출해 "모두 받기"를 구현
  const handleDownloadAll = async () => {
    if (!accessToken) {
      moveToLogin();
      return;
    }
    if (!targets.length || batchLoading) return;

    setBatchLoading(true);
    try {
      const results = await Promise.allSettled(
        targets.map((item) =>
          isCouponTab
            ? couponService.postCouponDownById(item.id)
            : eventService.downloadVoucherBook(item.id)
        )
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      if (ok) toast.success(`쿠폰 ${ok}장을 다운로드 받았습니다.`);
      if (fail) toast.error(`${fail}장은 다운로드하지 못했습니다.`);

      if (isCouponTab) {
        await fetchCouponItems();
      } else {
        await fetchVoucherItems();
      }
    } catch (error) {
      console.error(error);
      toast.error("다운로드에 실패했습니다.");
    } finally {
      setBatchLoading(false);
    }
  };

  return (
    <MobileViewContainer>
      <div className="sticky top-0 z-999 border-b border-gray-200 bg-white/80 backdrop-blur-md supports-[backdrop-filter]:bg-white/70">
        <div className="flex h-14 items-center px-2">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="뒤로가기"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-800 transition-colors hover:bg-gray-100 active:scale-[0.96] focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
          >
            <ChevronLeft size={24} strokeWidth={2} />
          </button>
          <h2 className="ml-1 text-base font-bold text-gray-900">쿠폰북</h2>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => navigate("/my-page?tab=coupon")}
            aria-label={hasUnused ? "내 쿠폰함 (안 쓴 쿠폰 있음)" : "내 쿠폰함"}
            className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-800 transition-colors hover:bg-gray-100 active:scale-[0.96] focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
          >
            <Gift size={22} strokeWidth={2} />
            {hasUnused ? (
              <span className="absolute right-1.5 top-1.5 flex h-2.5 w-2.5 items-center justify-center">
                <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 motion-safe:animate-ping" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
              </span>
            ) : null}
          </button>
        </div>
      </div>
      <div className="bg-white px-4 py-5 pb-28">
        <div className="mb-4">
          <h1 className="text-balance text-xl font-bold">쿠폰북 다운로드</h1>
          <p className="mt-1 text-pretty text-sm text-gray-500">
            {accessToken ? "무료쿠폰과 할인쿠폰을 다운로드하세요." : "목록만 확인할 수 있습니다. 다운로드는 로그인 후 가능합니다."}
          </p>
        </div>

        {/* 세그먼트 탭 */}
        <div className="mb-3 flex rounded-xl bg-gray-100 p-1">
          <button

            type="button"
            className={`flex-1 rounded-lg py-2.5 text-sm font-bold transition ${!isCouponTab ? "bg-white text-gray-900 shadow-sm" : "text-gray-400"}`}
            onClick={() => handleTabChange(TAB.VOUCHER)}
          >
            무료쿠폰북
          </button>
          <button
            type="button"
            className={`flex-1 rounded-lg py-2.5 text-sm font-bold transition ${isCouponTab ? "bg-white text-gray-900 shadow-sm" : "text-gray-400"}`}
            onClick={() => handleTabChange(TAB.COUPON)}
          >
            할인쿠폰북
          </button>
        </div>

        {/* 상단 요약: 안 받은 쿠폰이 있으면 "모두 받기", 다 받았으면 완료 안내 */}
        {accessToken && !isLoading && currentItems.length > 0 ? (
          targets.length > 0 ? (
            <div className="mb-2 flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
              <span className="text-sm font-bold text-gray-900">
                안 받은 쿠폰 <span className="text-[#e16036] tabular-nums">{targets.length}장</span>
              </span>
              <button
                type="button"
                onClick={handleDownloadAll}
                disabled={batchLoading}
                className="rounded-full bg-[#e16036]/10 px-3.5 py-2 text-xs font-bold text-[#e16036] transition active:scale-[0.96] disabled:opacity-60"
              >
                {batchLoading ? "받는 중…" : "모두 받기"}
              </button>
            </div>
          ) : (
            <div className="mb-2 flex items-center gap-2 rounded-xl bg-gray-50 px-4 py-3">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <Check size={13} strokeWidth={3} />
              </span>
              <span className="text-sm font-bold text-gray-900">쿠폰을 모두 받았어요</span>
            </div>
          )
        ) : null}

        {isLoading ? (
          <div className="py-16 text-center text-gray-500">로딩중...</div>
        ) : isCouponTab ? (
          couponItems.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 py-12 text-center text-gray-400">
              다운로드 가능한 할인쿠폰이 없습니다.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {couponItems.map((item) => {
                const key = `coupon-${item.id}`;
                const { value, unit } = getDiscountValue(item.coupon_product);
                const soldOut = item.remain === 0;
                const done = item.downloaded;
                const hasBadge = item.is_new || soldOut;

                return (
                  <div
                    key={item.id}
                    className={`flex gap-3 py-3.5 transition-opacity ${done ? "opacity-60" : ""}`}
                  >
                    <div className="flex w-14 shrink-0 flex-col items-center justify-center text-center">
                      <div className="font-black leading-none tracking-tight text-[#e16036] tabular-nums">
                        <span className={value.length > 3 ? "text-sm" : "text-lg"}>{value}</span>
                        <span className="text-xs">{unit}</span>
                      </div>
                      <div className="mt-1 text-[10px] font-semibold text-gray-700">할인</div>
                    </div>

                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <div className="flex items-start gap-1.5">
                        <div className="line-clamp-2 min-w-0 flex-1 text-pretty text-sm font-bold text-gray-900">
                          {item.coupon_product.title}
                        </div>
                        {hasBadge ? (
                          <span className="mt-0.5 inline-flex shrink-0 items-center gap-1">
                            <StatusBadges isNew={item.is_new} soldOut={soldOut} />
                          </span>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-700">
                        <span>{item.coupon_product.min_purchase?.toLocaleString?.() || 0}원 이상 구매 시</span>
                      </div>
                      <div className="space-y-0.5 text-[11px] text-gray-700 tabular-nums">
                        <div>만료 {item.coupon_product.expire_date || "없음"}</div>
                        <div>
                          다운로드 ~{item.end_date}
                          {accessToken ? ` · 잔여 ${item.remain}개` : ""}
                        </div>
                      </div>
                    </div>

                    {accessToken ? (
                      <div className="flex items-center">
                        <GetButton
                          done={done}
                          loading={downloadingKey === key}
                          disabled={done || downloadingKey === key || !item.downloadable}
                          onClick={() => handleCouponDownload(item)}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )
        ) : voucherItems.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 py-12 text-center text-gray-400">
            다운로드 가능한 무료쿠폰이 없습니다.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {voucherItems.map((item) => {
              const key = `voucher-${item.id}`;
              const status = voucherStatusMap[item.id] || { downloaded: false, remain: item.stock };
              const soldOut = status.remain === 0;
              const done = status.downloaded;
              const hasBadge = soldOut;
              const expireText = item.voucher_product.expire_date
                ? item.voucher_product.expire_date
                : item.voucher_product.expire_days
                  ? `발급일로부터 ${item.voucher_product.expire_days}일`
                  : "없음";

              return (
                <div
                  key={item.id}
                  className={`flex gap-3 py-3.5 transition-opacity ${done ? "opacity-60" : ""}`}
                >
                  <div className="flex w-14 shrink-0 flex-col items-center justify-center text-center">
                    <div className="text-sm font-black leading-tight text-[#e16036]">무료</div>
                    <div className="mt-1 text-[10px] font-semibold text-gray-700">쿠폰</div>
                  </div>

                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <div className="flex items-start gap-1.5">
                      <div className="line-clamp-2 min-w-0 flex-1 text-pretty text-sm font-bold text-gray-900">
                        {item.voucher_product.name}
                      </div>
                      {hasBadge ? (
                        <span className="mt-0.5 inline-flex shrink-0 items-center gap-1">
                          <StatusBadges isNew={false} soldOut={soldOut} />
                        </span>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-700">
                      <span>만료 {expireText}</span>
                    </div>
                    <div className="text-[11px] text-gray-700 tabular-nums">
                      다운로드 ~{item.end_date}
                      {accessToken ? ` · 잔여 ${status.remain}개` : ""}
                    </div>
                  </div>

                  {accessToken ? (
                    <div className="flex items-center">
                      <GetButton
                        done={done}
                        loading={downloadingKey === key}
                        disabled={done || downloadingKey === key}
                        onClick={() => handleVoucherDownload(item)}
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </MobileViewContainer>
  );
};

export default CouponBookDownloadPage;
